import { useMemo, useState } from "react";
import { supabase } from "./supabaseClient";

const card = { background: "#111", border: "1px solid #2a2a2a", borderRadius: 12, padding: 14, marginBottom: 16 };
const input = { width: "100%", boxSizing: "border-box", background: "#0b0b0b", border: "1px solid #303030", borderRadius: 9, color: "#fff", padding: "10px 12px", fontSize: 12, outline: "none" };
const btn = { borderRadius: 9, padding: "10px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer" };
const OFFICIAL_LOGO = "/email-footer-logo-v1.png?v=20260927-4";

const loadImage = src => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = src;
});

const fileImage = file => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
  image.onerror = error => { URL.revokeObjectURL(url); reject(error); };
  image.src = url;
});

const brandedFile = async file => {
  const [source, logo] = await Promise.all([fileImage(file), loadImage(OFFICIAL_LOGO)]);
  const canvas = document.createElement("canvas");
  canvas.width = source.naturalWidth || source.width;
  canvas.height = source.naturalHeight || source.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo preparar la imagen");
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

  const margin = Math.max(16, Math.round(Math.min(canvas.width, canvas.height) * 0.025));
  const targetW = Math.max(84, Math.min(230, Math.round(canvas.width * 0.18)));
  const targetH = Math.max(1, Math.round(targetW * ((logo.naturalHeight || logo.height) / (logo.naturalWidth || logo.width))));
  ctx.save();
  ctx.globalAlpha = 0.88;
  ctx.drawImage(logo, canvas.width - targetW - margin, canvas.height - targetH - margin, targetW, targetH);
  ctx.restore();

  const outputType = ["image/png", "image/webp", "image/jpeg"].includes(file.type) ? file.type : "image/jpeg";
  const blob = await new Promise((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error("No se pudo generar la imagen con logo")), outputType, 0.92));
  return new File([blob], file.name, { type: outputType, lastModified: Date.now() });
};

const Rating = ({ label, value, setValue, max = 5 }) => <label style={{ display: "grid", gap: 4 }}>
  <span style={{ color: "#777", fontSize: 9, fontWeight: 800 }}>{label}</span>
  <select value={value} onChange={event => setValue(Number(event.target.value))} style={input}>
    {Array.from({ length: max }, (_, index) => index + 1).map(number => <option key={number} value={number}>{number}</option>)}
  </select>
</label>;

export default function AdminOfficialReviewPanel({ user, places = [], onCreated, setSessionToast }) {
  const isOfficial = String(user?.username || "").toLowerCase() === "venaqqi";
  const availablePlaces = useMemo(() => places.filter(place => place && place.id && place.metadata?.rejected !== true && place.moderation_status !== "rejected").sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "es")), [places]);
  const [open, setOpen] = useState(false);
  const [placeId, setPlaceId] = useState("");
  const [platillo, setPlatillo] = useState("");
  const [comentario, setComentario] = useState("");
  const [sabor, setSabor] = useState(4);
  const [servicio, setServicio] = useState(4);
  const [satisfaccion, setSatisfaccion] = useState(4);
  const [precio, setPrecio] = useState(3);
  const [totalBill, setTotalBill] = useState("");
  const [partySize, setPartySize] = useState("");
  const [brandImages, setBrandImages] = useState(true);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!isOfficial) return null;

  const upload = async files => {
    const list = Array.from(files || []).slice(0, Math.max(0, 6 - images.length));
    if (!list.length) return;
    setUploading(true); setError("");
    try {
      const uploaded = [];
      for (const original of list) {
        const file = brandImages ? await brandedFile(original) : original;
        const form = new FormData();
        form.append("file", file);
        form.append("upload_preset", "venaqqi_reviews");
        const response = await fetch("https://api.cloudinary.com/v1_1/dgdtlohqw/image/upload", { method: "POST", body: form });
        const data = await response.json();
        if (!response.ok || !data.secure_url) throw new Error(data?.error?.message || "Cloudinary rechazó la imagen");
        uploaded.push({ url: data.secure_url, name: original.name, branded: brandImages, public_id: data.public_id || null });
      }
      setImages(current => [...current, ...uploaded]);
    } catch (err) { setError(err?.message || "No se pudieron subir las imágenes"); }
    finally { setUploading(false); }
  };

  const publish = async () => {
    setError("");
    if (!placeId) { setError("Selecciona un lugar."); return; }
    if (!platillo.trim() && !comentario.trim()) { setError("Escribe al menos el platillo o un comentario."); return; }
    setSaving(true);
    try {
      const { data, error: rpcError } = await supabase.rpc("admin_publish_official_review", {
        p_placeid: Number(placeId), p_sabor: sabor, p_precio: precio, p_servicio: servicio, p_satisfaccion: satisfaccion,
        p_platillo: platillo.trim(), p_comentario: comentario.trim(), p_precio_label: null,
        p_total_bill: totalBill ? Number(totalBill) : null, p_party_size: partySize ? Number(partySize) : null,
        p_imagenes: images,
      });
      if (rpcError) throw rpcError;
      onCreated?.(data);
      setPlatillo(""); setComentario(""); setTotalBill(""); setPartySize(""); setImages([]);
      setSessionToast?.({ type: "success", text: "✅ Publicado como Venaqqi" });
      setTimeout(() => setSessionToast?.(null), 3000);
    } catch (err) { setError(err?.message || "No se pudo publicar"); }
    finally { setSaving(false); }
  };

  return <div style={{ ...card, borderColor: open ? "#7f1d1d" : "#2a2a2a" }}>
    <button type="button" onClick={() => setOpen(value => !value)} style={{ width: "100%", background: "none", border: 0, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", padding: 0 }}>
      <span style={{ fontSize: 13, fontWeight: 900 }}>✍️ PUBLICAR COMO VENAQQI</span><span style={{ color: "#e53935", fontSize: 11 }}>{open ? "Cerrar ▲" : "Abrir ▼"}</span>
    </button>
    {open && <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
      <select value={placeId} onChange={event => setPlaceId(event.target.value)} style={input}><option value="">Selecciona un lugar</option>{availablePlaces.map(place => <option key={place.id} value={place.id}>{place.name}</option>)}</select>
      <input value={platillo} onChange={event => setPlatillo(event.target.value)} placeholder="¿Qué comiste?" maxLength={120} style={input} />
      <div style={{ position: "relative" }}><textarea value={comentario} onChange={event => setComentario(event.target.value.slice(0, 100))} placeholder="Comentario oficial (máx. 100 caracteres)" rows={3} style={{ ...input, resize: "vertical" }} /><span style={{ position: "absolute", right: 9, bottom: 7, color: comentario.length >= 90 ? "#e53935" : "#555", fontSize: 9 }}>{comentario.length}/100</span></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8 }}><Rating label="SABOR" value={sabor} setValue={setSabor} /><Rating label="SERVICIO" value={servicio} setValue={setServicio} /><Rating label="SATISFACCIÓN" value={satisfaccion} setValue={setSatisfaccion} /><Rating label="PRECIO" value={precio} setValue={setPrecio} max={7} /></div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8 }}><input value={totalBill} onChange={event => setTotalBill(event.target.value)} inputMode="decimal" placeholder="Cuenta total MXN" style={input} /><input value={partySize} onChange={event => setPartySize(event.target.value)} inputMode="numeric" placeholder="Personas" style={input} /></div>
      <div style={{ background: "#0b0b0b", border: "1px solid #242424", borderRadius: 10, padding: 10 }}>
        <label style={{ color: "#ddd", fontSize: 11, display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><input type="checkbox" checked={brandImages} onChange={event => setBrandImages(event.target.checked)} /> Añadir logo PNG de Venaqqi a las fotos</label>
        <label style={{ display: "block", textAlign: "center", background: "#161616", border: "1px solid #333", borderRadius: 9, padding: 10, color: "#90caf9", fontSize: 11, fontWeight: 800, cursor: uploading ? "wait" : "pointer" }}>{uploading ? "Subiendo…" : `📷 Subir fotos (${images.length}/6)`}<input type="file" accept="image/*" multiple disabled={uploading || images.length >= 6} onChange={event => upload(event.target.files)} style={{ display: "none" }} /></label>
        {images.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 6, marginTop: 8 }}>{images.map((image, index) => <div key={`${image.url}-${index}`} style={{ position: "relative" }}><img src={image.url} alt="" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 7 }} /><button type="button" onClick={() => setImages(current => current.filter((_, i) => i !== index))} style={{ position: "absolute", right: 3, top: 3, width: 22, height: 22, borderRadius: "50%", border: 0, background: "rgba(0,0,0,.75)", color: "#fff", cursor: "pointer" }}>×</button></div>)}</div>}
      </div>
      {error && <div style={{ color: "#ff8a80", background: "#351515", border: "1px solid #5a2525", borderRadius: 9, padding: 9, fontSize: 10 }}>{error}</div>}
      <button type="button" onClick={publish} disabled={saving || uploading} style={{ ...btn, background: "#e53935", color: "#fff", border: "1px solid #e53935", opacity: saving || uploading ? .55 : 1 }}>{saving ? "Publicando…" : "Publicar inmediatamente"}</button>
      <p style={{ color: "#555", fontSize: 9, margin: 0, lineHeight: 1.4 }}>Solo disponible para Master Admin con el perfil oficial venaqqi. Estas publicaciones quedan aprobadas de inmediato y las imágenes se guardan en Cloudinary.</p>
    </div>}
  </div>;
}
